import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Text, Anchor, Group } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import '@fontsource/orbitron/700.css';

import CalendarMasterDetail from '../../../CalendarMasterDetail/CalendarMasterDetail';
import RepairsPersonSelect from './RepairsPersonSelect'; // New component for person selection
import RepairsIndividualPerformancePanel from './RepairsIndividualPerformancePanel'; // New component for individual scorecard

import RFQDayDetails from './calendar/RFQDayDetails.jsx';
import PODayDetails from './calendar/PODayDetails.jsx';
import CallTrackerDetails from './calendar/CallTrackerDetails.jsx';
import PriceUpdateDetails from './calendar/PriceUpdateDetails.jsx';

import MembershipPopup from '../MembershipPopup.jsx';
import SQLEditorPopup from '../SQLEditorPopup.jsx';
import config from '../../../../config.js';

const RepairsIndividualScorecardTabSet = () => {
  const [activeTab, setActiveTab] = useState('0');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for technical pop-ups
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);

  const [searchParams] = useSearchParams();
  const defaultPersonCode = searchParams.get('code');
  const defaultTab = searchParams.get('tab');

  // Set the initial active tab based on the 'tab' parameter in the URL
  useEffect(() => {
    if (defaultTab) {
      const tabIndex = parseInt(defaultTab, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) {
        setActiveTab(tabIndex.toString());
      }
    }
  }, [defaultTab]);

  // Load calendar data for selected person
  const loadPersonCalendarData = async () => {
    if (!selectedPerson) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/supply-chain/repairs/individual/calendar-activity?ccode=${selectedPerson.cCode}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch calendar data');
      }
      
      const data = await response.json();
      setCalendarData(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading calendar data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data when person changes or tab becomes active
  useEffect(() => {
    if (activeTab === '1' && selectedPerson) {
      loadPersonCalendarData();
    }
  }, [activeTab, selectedPerson]);

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    
    // Load calendar data when switching to activity tab
    if (newValue === '1' && selectedPerson) {
      loadPersonCalendarData();
    }
  };

  const handlePersonSelect = (person) => {
    console.log('Person selected in TabSet:', person);
    setSelectedPerson(person);
  };

  // Activities configuration for individual-level data
  const individualActivitiesConfig = useMemo(() => [
    {
      type: 'RFQs',
      color: '#1976d2',
      dataFields: {
        docCount: 'totalRFQDocCount',
        lineCount: 'totalRFQLineCount',
        total: 'totalRFQDollars',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: RFQDayDetails,
    },
    {
      type: 'POs',
      color: '#2D875D',
      dataFields: {
        docCount: 'poDocCount',
        lineCount: 'poLineCount',
        total: 'poDollars',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: PODayDetails,
    },
    {
      type: 'Price Updates',
      color: '#A23E48',
      dataFields: {
        lineCount: 'sellPricesUpdated',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: PriceUpdateDetails,
    },
    {
      type: 'Call Trackers',
      color: '#9C4EE5',
      dataFields: {
        lineCount: 'callTrackerDocCount',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: CallTrackerDetails,
    },
  ], []);

  // Format the calendar data
  const formattedCalendarData = useMemo(() => {
    if (!calendarData || !Array.isArray(calendarData)) return [];
    
    console.log("Processing individual calendar data:", calendarData);
    return calendarData.map((item) => ({
      date: item.date,
      ...item,
    }));
  }, [calendarData]);

  // Key for forcing calendar re-render
  const calendarKey = calendarData && calendarData.length > 0 
    ? `calendar-${selectedPerson?.cCode}-${calendarData.length}` 
    : 'calendar-empty';

  return (
    <Box style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      {/* Person Title and Select */}
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '16px',
          paddingLeft: '16px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Text
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
          }}
        >
          {selectedPerson
            ? `${selectedPerson.fullName}`
            : 'No Person Selected'}
        </Text>

        <RepairsPersonSelect
          onPersonSelect={handlePersonSelect}
          defaultPersonCode={defaultPersonCode}
        />
      </Box>



      {/* Tabs with Content */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        styles={{
          tab: {
            color: 'var(--mantine-color-gray-6)',
            '&[data-active]': {
              color: 'var(--mantine-color-white)',
              fontWeight: 'bold',
            },
          },
          tabsList: {
            borderBottom: 'none',
          },
        }}
        style={{ flexGrow: 1, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Tabs.List>
          <Tabs.Tab value="0">
            Individual Scorecard
          </Tabs.Tab>
          {/*
          <Tabs.Tab value="1">
            RFQs, POs, Price Updates, Call Trackers
          </Tabs.Tab>
          */}
        </Tabs.List>

        <Tabs.Panel value="0" style={{ flexGrow: 1, overflow: 'hidden' }}>
          <RepairsIndividualPerformancePanel selectedPerson={selectedPerson} />
        </Tabs.Panel>
        
        <Tabs.Panel value="1" style={{ flexGrow: 1, overflow: 'hidden' }}>
          {selectedPerson ? (
            loading ? (
              <Text p="md">
                Loading Calendar Data...
              </Text>
            ) : error ? (
              <Text p="md" c="red">
                Error loading calendar data: {error}
              </Text>
            ) : (
              <>
                {/* Technical options: small links for debugging */}
                <Group mb="sm" gap="sm" p="sm">
                  <Anchor
                    size="xs"
                    c="dimmed"
                    onClick={() => setOpenMembership(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    Individual Info
                  </Anchor>
                  <Anchor
                    size="xs"
                    c="dimmed"
                    onClick={() => setOpenSummaryQuery(true)}
                    style={{ cursor: 'pointer' }}
                  >
                    Calendar Summary Query
                  </Anchor>
                </Group>

                <CalendarMasterDetail
                  key={calendarKey}
                  businessKey={selectedPerson.cCode}
                  data={formattedCalendarData}
                  currentYear={new Date().getFullYear()}
                  currentMonth={new Date().getMonth()}
                  onMonthChange={(year, month) => {
                    console.log(`Month changed to: ${month + 1}/${year}`);
                    loadPersonCalendarData();
                  }}
                  activities={individualActivitiesConfig}
                />
              </>
            )
          ) : (
            <Text p="md">
              Please select a person to view calendar data.
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Pop-up dialogs */}
      <MembershipPopup 
        opened={openMembership} 
        onClose={() => setOpenMembership(false)} 
      />
      <SQLEditorPopup
        opened={openSummaryQuery}
        onClose={() => setOpenSummaryQuery(false)}
        title="Individual Calendar Summary Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/individual/queries/calendar/calendar-summary-query?ccode=${selectedPerson?.cCode || ''}`}
      />
    </Box>
  );
};

export default RepairsIndividualScorecardTabSet;